<template>
  <v-main>
    <v-container fluid>
      <!-- Header Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Account Delete Requests</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="5">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search"
                    label="Search by name, phone, email..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="clearSearch"
                    @keydown.enter="getData"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    clearable
                    @click:clear="clearStatus"
                    @input="getData"
                    prepend-inner-icon="mdi-filter"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="600px" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">User</th>
                      <th class="text-left">Phone</th>
                      <th class="text-left">Email</th>
                      <th class="text-left">Reason</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Requested At</th>
                      <th class="text-left">Processed By</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="requests.length">
                    <tr
                      v-for="(item, i) in requests"
                      :key="`req_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': i === requests.length - 1,
                      }"
                    >
                      <td style="min-width: 70px">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                      <td style="min-width: 140px">{{ item.name }}</td>
                      <td>{{ item.phone }}</td>
                      <td>{{ item.email || '-' }}</td>
                      <td style="max-width: 200px">
                        <span v-if="item.reason" class="text-truncate d-block" style="max-width:180px">
                          {{ item.reason }}
                        </span>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td>
                        <v-chip small :color="getStatusColor(item.status)" text-color="white">
                          {{ getStatusLabel(item.status) }}
                        </v-chip>
                      </td>
                      <td style="min-width: 130px">{{ item.created_at | niceDate }}</td>
                      <td>
                        <span v-if="item.processed_by">
                          {{ item.processed_by.first_name }} {{ item.processed_by.last_name }}
                        </span>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td class="text-right" style="min-width: 140px">
                        <!-- Approve -->
                        <v-tooltip bottom v-if="item.status == '1'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="success"
                              class="mr-1"
                              @click="openApproveDialog(item)"
                            >
                              <v-icon dark>mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve & Delete Account</span>
                        </v-tooltip>
                        <!-- Reject -->
                        <v-tooltip bottom v-if="item.status == '1'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="warning"
                              class="mr-1"
                              @click="openRejectDialog(item)"
                            >
                              <v-icon dark>mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject Request</span>
                        </v-tooltip>
                        <!-- Delete record -->
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="openDeleteDialog(item.id)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete Record</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9" class="text-center pa-6">
                        <v-alert type="info" text>No delete requests found.</v-alert>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Approve Confirm Dialog -->
    <v-dialog v-model="approveDialog" max-width="500" persistent>
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: #e53935; font-size: 80px">mdi-account-remove</v-icon>
            </v-col>
            <v-col sm="9">
              <v-card-title class="pa-0 mb-2">Approve Deletion?</v-card-title>
              <v-card-text class="pa-0">
                <p>
                  Account of <strong>{{ selectedRequest && selectedRequest.name }}</strong>
                  ({{ selectedRequest && selectedRequest.phone }}) will be permanently deactivated.
                </p>
                <v-textarea
                  v-model="adminNotes"
                  label="Admin Notes (optional)"
                  outlined
                  rows="2"
                  auto-grow
                ></v-textarea>
              </v-card-text>
              <v-card-actions class="pa-0 justify-end">
                <v-btn text @click="approveDialog = false">Cancel</v-btn>
                <v-btn color="error" :loading="isActing" @click="approveRequest">
                  Approve & Delete
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog" max-width="500" persistent>
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: orange; font-size: 80px">mdi-close-circle</v-icon>
            </v-col>
            <v-col sm="9">
              <v-card-title class="pa-0 mb-2">Reject Request?</v-card-title>
              <v-card-text class="pa-0">
                <p>
                  Rejection reason will be sent to
                  <strong>{{ selectedRequest && selectedRequest.name }}</strong>.
                </p>
                <v-textarea
                  v-model="adminNotes"
                  label="Reason for rejection (optional)"
                  outlined
                  rows="2"
                  auto-grow
                ></v-textarea>
              </v-card-text>
              <v-card-actions class="pa-0 justify-end">
                <v-btn text @click="rejectDialog = false">Cancel</v-btn>
                <v-btn color="warning" :loading="isActing" @click="rejectRequest">
                  Reject
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Delete Record Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: orange; font-size: 80px">mdi-alert</v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h2 style="color: orange">Confirm Delete</h2>
                <br />
                <h3>Delete this request record permanently?</h3>
                <br />
                <v-btn text @click="deleteDialog = false" class="mr-2">Cancel</v-btn>
                <v-btn color="error" :loading="isActing" @click="deleteRecord">Delete</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: green; font-size: 90px">mdi-check-circle</v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: green">Done!</h1>
                <br />
                <h3>{{ successMessage }}</h3>
                <br />
                <v-btn color="success" @click="successDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: red; font-size: 90px">mdi-alert-circle</v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: red">Error!</h1>
                <br />
                <h3>{{ errorMessage }}</h3>
                <br />
                <v-btn color="error" @click="errorDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "AccountDeleteRequestsPage",
  data() {
    return {
      isLoading: false,
      isActing: false,
      requests: [],
      page: 1,
      paginationLength: 0,
      count: 0,
      filter: {
        search: null,
        status: null,
      },
      selectedRequest: null,
      adminNotes: "",
      deleteRecordId: null,
      approveDialog: false,
      rejectDialog: false,
      deleteDialog: false,
      successDialog: false,
      errorDialog: false,
      successMessage: "",
      errorMessage: "",
      statusOptions: [
        { text: "Pending", value: "1" },
        { text: "Approved", value: "2" },
        { text: "Completed (Deleted)", value: "3" },
        { text: "Rejected", value: "4" },
        { text: "Cancelled", value: "5" },
      ],
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.rowsPerPage,
        };
        if (this.filter.search) params.search = this.filter.search;
        if (this.filter.status) params.status = this.filter.status;

        const response = await axios.get("/account-delete-requests", { params });
        this.requests = response.data.data || [];
        this.count = response.data.count || 0;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error(error);
        this.showError("Failed to load data.");
      } finally {
        this.isLoading = false;
      }
    },

    openApproveDialog(item) {
      this.selectedRequest = item;
      this.adminNotes = "";
      this.approveDialog = true;
    },

    openRejectDialog(item) {
      this.selectedRequest = item;
      this.adminNotes = "";
      this.rejectDialog = true;
    },

    openDeleteDialog(id) {
      this.deleteRecordId = id;
      this.deleteDialog = true;
    },

    async approveRequest() {
      this.isActing = true;
      try {
        const response = await axios.post(
          `/account-delete-requests/${this.selectedRequest.id}/approve`,
          { admin_notes: this.adminNotes }
        );
        if (response.data.success) {
          this.approveDialog = false;
          this.showSuccess("Account deleted successfully.");
          this.getData();
        }
      } catch (error) {
        this.approveDialog = false;
        this.showError(error.response?.data?.message || "Failed to approve request.");
      } finally {
        this.isActing = false;
      }
    },

    async rejectRequest() {
      this.isActing = true;
      try {
        const response = await axios.post(
          `/account-delete-requests/${this.selectedRequest.id}/reject`,
          { admin_notes: this.adminNotes }
        );
        if (response.data.success) {
          this.rejectDialog = false;
          this.showSuccess("Request rejected successfully.");
          this.getData();
        }
      } catch (error) {
        this.rejectDialog = false;
        this.showError(error.response?.data?.message || "Failed to reject request.");
      } finally {
        this.isActing = false;
      }
    },

    async deleteRecord() {
      this.isActing = true;
      try {
        const response = await axios.delete(`/account-delete-requests/${this.deleteRecordId}`);
        if (response.data.success) {
          this.deleteDialog = false;
          this.showSuccess("Record deleted.");
          this.getData();
        }
      } catch (error) {
        this.deleteDialog = false;
        this.showError(error.response?.data?.message || "Failed to delete record.");
      } finally {
        this.isActing = false;
      }
    },

    clearSearch() {
      this.filter.search = null;
      this.page = 1;
      this.getData();
    },

    clearStatus() {
      this.filter.status = null;
      this.page = 1;
      this.getData();
    },

    getStatusColor(status) {
      const colors = {
        "1": "orange darken-2",
        "2": "blue",
        "3": "red darken-2",
        "4": "grey",
        "5": "teal",
      };
      return colors[status] || "grey";
    },

    getStatusLabel(status) {
      const labels = {
        "1": "Pending",
        "2": "Approved",
        "3": "Deleted",
        "4": "Rejected",
        "5": "Cancelled",
      };
      return labels[status] || status;
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    showSuccess(msg) {
      this.successMessage = msg;
      this.successDialog = true;
    },

    showError(msg) {
      this.errorMessage = msg;
      this.errorDialog = true;
    },
  },
};
</script>

<style scoped>
.striped-table tbody tr.table-row-even {
  background-color: #f5f5f5;
}
.striped-table tbody tr.table-row-odd {
  background-color: #ffffff;
}
</style>
